/**
 * Museum Academics Gallery Component
 * Custom Element: <museum-academics-gallery>
 */
if (!customElements.get('museum-academics-gallery')) {
  class MuseumAcademicsGallery extends HTMLElement {
    constructor() {
      super();

      this.track = null;
      this.prevBtn = null;
      this.nextBtn = null;
      this.autoplayTimer = null;
      this.isPaused = false;

      this.handlePrev = this.scrollPrev.bind(this);
      this.handleNext = this.scrollNext.bind(this);
      this.handleMouseEnter = this.onMouseEnter.bind(this);
      this.handleMouseLeave = this.onMouseLeave.bind(this);
      this.handleFocusIn = this.onFocusIn.bind(this);
      this.handleFocusOut = this.onFocusOut.bind(this);
      this.handleVisibilityChange = this.onVisibilityChange.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.museum-academics__track');
      this.prevBtn = this.querySelector('.museum-academics__arrow--prev');
      this.nextBtn = this.querySelector('.museum-academics__arrow--next');

      if (!this.track) return;

      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.speed, 10) || 5000;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.loop = this.dataset.loop === 'true';

      this.bindEvents();

      if (this.autoplayEnabled && !this.prefersReducedMotion()) {
        this.startAutoplay();
      }
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.unbindEvents();
    }

    bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', this.handlePrev);
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', this.handleNext);
      }

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', this.handleMouseEnter);
        this.addEventListener('mouseleave', this.handleMouseLeave);
      }

      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    unbindEvents() {
      if (this.prevBtn) {
        this.prevBtn.removeEventListener('click', this.handlePrev);
      }
      if (this.nextBtn) {
        this.nextBtn.removeEventListener('click', this.handleNext);
      }

      if (this.pauseOnHover) {
        this.removeEventListener('mouseenter', this.handleMouseEnter);
        this.removeEventListener('mouseleave', this.handleMouseLeave);
      }

      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    getCardWidth() {
      const firstCard = this.querySelector('.museum-academics__card-wrap');
      if (!firstCard) return 300;
      const gap = parseInt(getComputedStyle(this).getPropertyValue('--mag-card-gap'), 10) || 20;
      return firstCard.offsetWidth + gap;
    }

    scrollNext() {
      if (!this.track) return;
      const cardWidth = this.getCardWidth();
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;

      if (this.loop && this.track.scrollLeft >= maxScroll - 10) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        this.track.scrollBy({ left: cardWidth, behavior: 'smooth' });
      }
    }

    scrollPrev() {
      if (!this.track) return;
      const cardWidth = this.getCardWidth();

      if (this.loop && this.track.scrollLeft <= 10) {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        this.track.scrollTo({ left: maxScroll, behavior: 'smooth' });
      } else {
        this.track.scrollBy({ left: -cardWidth, behavior: 'smooth' });
      }
    }

    onMouseEnter() {
      if (this.pauseOnHover) {
        this.isPaused = true;
        this.stopAutoplay();
      }
    }

    onMouseLeave() {
      if (this.pauseOnHover) {
        this.isPaused = false;
        if (this.autoplayEnabled && !this.prefersReducedMotion()) {
          this.startAutoplay();
        }
      }
    }

    onFocusIn() {
      this.isPaused = true;
      this.stopAutoplay();
    }

    onFocusOut(event) {
      if (!this.contains(event.relatedTarget)) {
        this.isPaused = false;
        if (this.autoplayEnabled && !this.prefersReducedMotion()) {
          this.startAutoplay();
        }
      }
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else if (this.autoplayEnabled && !this.isPaused && !this.prefersReducedMotion()) {
        this.startAutoplay();
      }
    }

    startAutoplay() {
      if (this.autoplayTimer) return;
      this.autoplayTimer = setInterval(() => {
        this.scrollNext();
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  }

  customElements.define('museum-academics-gallery', MuseumAcademicsGallery);
}
